import React, { useContext, useEffect } from "react";
import { FormikErrors, useFormik } from "formik";
import { useAlert } from 'react-alert';
import { useHistory } from "react-router-dom";
import { APourRole } from "../common/types/Types";
import { EntitySelect } from "../common/inputs/EntitySelect";
import Loader from "../common/views/Loader";
import { getUserSchema } from "../common/types/Schemas";
import { BYInput } from "../common/inputs/BYInput";
import { AuthContext } from "../providers/AuthProvider";
import { useUpdateUser, useUser } from "../api/hooks";
import { Animated } from "react-animated-css";
import Footer from "../common/views/Footer";
import BYSwitch from "../common/inputs/BYSwitch";
import { BYTable } from "../common/views/BYTable";
import { BYPasswordInput } from "../common/inputs/BYPasswordInput";

interface UserIndexProps {
    match: {
        params: {
            id: string;
            idOperation: string;
        }
    }
}

export const UserIndex = (props: UserIndexProps) => {
    const { isAdmin } = useContext(AuthContext);
    const alert = useAlert();
    const history = useHistory();
    const userId = Number(props.match.params.id);
    const operationId = props.match.params.idOperation ? Number(props.match.params.idOperation) : undefined;
    const isMe = props.match.params.id === 'me';
    const isNew = props.match.params.id === 'new';

    const { data: user, isLoading, isRefetching } = useUser(userId, isMe);
    const { mutateAsync: mutateUser } = useUpdateUser(isMe);

    const canEditUser = isAdmin() || isMe;

    const formik = useFormik({
        initialValues: {
            login: '',
            email: '',
            prenom: '',
            nom: '',
            adresse: '',
            complAdresse: '',
            nomEntreprise: '',
            ville: '',
            indicActif: true,
            password: '',
            passwordConfirmation: '',
            aPourRoles: (operationId ? [{ operationRefOperation: operationId }] : []) as APourRole[]
        },
        validationSchema: getUserSchema(isNew),
        onSubmit: async (values) => {
            try {
                const filteredValues = {
                    ...values,
                    aPourRoles: values.aPourRoles.filter(ap => ap.deleted || ap.new || ap.updated),
                };
                await mutateUser({ userId: userId || user?.refUti, values: filteredValues });

                alert.success('Utilisateur mis à jour');

                if (history.length) {
                    return history.goBack();
                }

                if (isMe) {
                    return history.push("/");
                }

                history.push("/user");
            } catch (error) {
                const statusCode = error.response && error.response.status;

                if (statusCode === 401) {
                    alert.error('Valeurs invalides ou incomplètes');
                } else if (statusCode === 409) {
                    alert.error(error.response.data);
                } else {
                    alert.error(error.message);
                }
            }
        }
    });

    useEffect(() => {
        if (user) {
            formik.setValues({
                login: user.login,
                email: user.email,
                prenom: user.prenom,
                nom: user.nom,
                nomEntreprise: user.nomEntreprise || '',
                adresse: user.adresse,
                complAdresse: user.complAdresse,
                ville: user.ville,
                indicActif: user.indicActif,
                password: '',
                passwordConfirmation: '',
                aPourRoles: user.aPourRoles
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    if (isLoading || isRefetching) {
        return <div className="text-center my-6"><Loader /></div>;
    }

    const visibleAPR = formik.values.aPourRoles?.filter(ap => !ap.deleted);
    const hasValidationErrors = Object.keys(formik.errors).length > 0;

    // filter to delete duplicate Operation when user have multiple ROLE
    const visibleAPRUnique = visibleAPR.filter((value, index, self) => 
        index === self.findIndex((y) => (
            y.operationRefOperation === value.operationRefOperation
        )
    ))

    return (
        <Animated animationIn="fadeIn" animationOut="fadeOut" isVisible={true}>
            <div className="container">
                <form onSubmit={formik.handleSubmit} autoComplete="new-password">
                    <div className="row">
                        <div className="col-8">
                            <h4 className="my-4 color-green">{isMe ? 'Profil' : (isNew ? 'Création d\'utilisateur' : 'Modification d\'utilisateur')}</h4>
                        </div>
                        <div className="col-4 tittle-buttons">
                            {isAdmin() && <BYSwitch
                                title={'Active'}
                                disabled={isMe}
                                checked={formik.values.indicActif}
                                onChange={(newValue) => {
                                    formik.setValues({
                                        ...formik.values,
                                        indicActif: newValue
                                    });
                                }}
                            />}
                        </div>
                    </div>
                    <div className="input-container">
                        <div className="row mb-3">
                            <div className="col">
                                <BYInput
                                    type="email"
                                    name="email"
                                    title="E-mail *"
                                    className="form-control"
                                    disabled={!(isNew || canEditUser)}
                                    value={formik.values.email}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    required
                                    autoFocus
                                    touched={formik.touched.email || !isNew}
                                    error={formik.errors.email}
                                />
                            </div>
                            <div className="col">
                                <BYInput
                                    type="text"
                                    name="login"
                                    title="Identifiant *"
                                    className="form-control"
                                    disabled={!(isNew || canEditUser)}
                                    value={formik.values.login}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    required
                                    autoFocus
                                    touched={formik.touched.login || !isNew}
                                    error={formik.errors.login}
                                />
                            </div>
                        </div>


                        <div className="row mb-3">
                            <div className="col">
                                <BYInput
                                    type="text"
                                    name="prenom"
                                    title="Prénom *"
                                    className="form-control"
                                    value={formik.values.prenom}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    required
                                    disabled={!(isNew || canEditUser)}
                                    touched={formik.touched.prenom || !isNew}
                                    error={formik.errors.prenom}
                                />
                            </div>
                            <div className="col">
                                <BYInput
                                    type="text"
                                    name="nom"
                                    title="Nom *"
                                    className="form-control"
                                    value={formik.values.nom}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    required
                                    disabled={!(isNew || canEditUser)}
                                    touched={formik.touched.nom || !isNew}
                                    error={formik.errors.nom}
                                />
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col">
                                <BYInput
                                    type="text"
                                    name="adresse"
                                    title="Adresse"
                                    className="form-control" value={formik.values.adresse}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    touched={formik.touched.adresse || !isNew}
                                    error={formik.errors.adresse}
                                    disabled={!(isNew || canEditUser)}
                                />
                            </div>
                            <div className="col">
                                <BYInput
                                    type="text"
                                    name="complAdresse"
                                    title="Complement Adresse"
                                    className="form-control"
                                    value={formik.values.complAdresse}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    touched={formik.touched.complAdresse || !isNew}
                                    error={formik.errors.complAdresse}
                                    disabled={!(isNew || canEditUser)}
                                />
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col">
                                <BYInput
                                    type="text"
                                    name="ville"
                                    title="Ville"
                                    className="form-control"
                                    value={formik.values.ville}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    touched={formik.touched.ville || !isNew}
                                    error={formik.errors.ville}
                                    disabled={!(isNew || canEditUser)}
                                />
                            </div>
                            <div className="col">
                                <BYInput
                                    type="text"
                                    name="nomEntreprise"
                                    title="Entreprise *"
                                    className="form-control"
                                    value={formik.values.nomEntreprise}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    required={true}
                                    touched={formik.touched.nomEntreprise || !isNew}
                                    error={formik.errors.nomEntreprise}
                                    disabled={!(isNew || canEditUser)}
                                />
                            </div>
                        </div>
                    </div>

                    {(isNew || canEditUser) && <>
                        <h5 className="my-4 color-green">{isNew ? 'Mot de passe' : 'Changer mot de passe'}</h5>

                        <div className="input-container">

                            <div className="row mb-3">
                                <div className="col">
                                    <BYPasswordInput
                                        name="password"
                                        title="Nouveau mot de passe"
                                        autoComplete="new-password"
                                        value={formik.values.password}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        required={isNew}
                                        touched={formik.touched.password}
                                        error={formik.errors.password}
                                    />
                                </div>
                                <div className="col">
                                    <BYPasswordInput
                                        name="passwordConfirmation"
                                        title="Confirmation de mot de passe"
                                        autoComplete="off"
                                        value={formik.values.passwordConfirmation}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        required={isNew}
                                        touched={formik.touched.passwordConfirmation}
                                        error={formik.errors.passwordConfirmation}
                                    />
                                </div>
                            </div>
                        </div>
                    </>}


                    <BYTable
                        rowsPerPage={10}
                        containerClassName="mt-4"
                        loading={isLoading}
                        data={visibleAPRUnique}
                        columns={['operationRefOperation', 'refAPourRole', 'utilisateurRefUti']}
                        columnNames={['Rôle', 'Opération', '']}
                        dataTransform={{
                            'operationRefOperation': (aPourRoleValues) => {
                                const index = formik.values.aPourRoles.findIndex(ap => ap === aPourRoleValues);
                                const iTouched = formik.touched.aPourRoles ? formik.touched.aPourRoles[index] : {} as any;
                                const iErrors = formik.errors.aPourRoles ? formik.errors.aPourRoles[index] as FormikErrors<APourRole> : {} as any;
                                const isNewRecord = !aPourRoleValues.refAPourRole;
                                const canManageRecord = isAdmin() /*|| (managedOperationIds.includes(aPourRoleValues.operationRefOperation) && canManageOperationUsers)*/;

                                return <EntitySelect
                                    type="role"
                                    placeholder="Role"
                                    disabled={!(canManageRecord || isNewRecord)}
                                    value={aPourRoleValues.roleRefRole}
                                    onBlur={formik.handleBlur}
                                    onChange={(newValue) => {
                                        const newAPourRoles = [...formik.values.aPourRoles];
                                        newAPourRoles[index] = {
                                            ...aPourRoleValues,
                                            roleRefRole: newValue,
                                            updated: true
                                        };

                                        formik.setValues({
                                            ...formik.values,
                                            aPourRoles: newAPourRoles
                                        })
                                    }}
                                    required={true}
                                    error={iErrors?.roleRefRole}
                                    touched={iTouched?.roleRefRole}
                                />
                            },
                            'refAPourRole': (aPourRoleValues) => {

                                const index = formik.values.aPourRoles.findIndex(ap => ap === aPourRoleValues);
                                const iTouched = formik.touched.aPourRoles ? formik.touched.aPourRoles[index] : {} as any;
                                const iErrors = formik.errors.aPourRoles ? formik.errors.aPourRoles[index] as FormikErrors<APourRole> : {} as any;
                                const isNewRecord = !aPourRoleValues.refAPourRole;
                                const canManageRecord = isAdmin() /*|| (managedOperationIds.includes(aPourRoleValues.operationRefOperation) && canManageOperationUsers)*/;

                                return <EntitySelect
                                    type={'operation'}
                                    ignoreOperationIds={formik.values.aPourRoles.filter(a => a.operationRefOperation !== aPourRoleValues.operationRefOperation).map(a => a.operationRefOperation)}
                                    placeholder="Opération"
                                    disabled={!(canManageRecord || isNewRecord)}
                                    value={aPourRoleValues.operationRefOperation}
                                    onBlur={formik.handleBlur}
                                    onChange={(newValue) => {
                                        const newAPourRoles = [...formik.values.aPourRoles];
                                        newAPourRoles[index] = {
                                            ...aPourRoleValues,
                                            operationRefOperation: newValue,
                                            updated: true
                                        };

                                        formik.setValues({
                                            ...formik.values,
                                            aPourRoles: newAPourRoles
                                        })
                                    }}
                                    required={true}
                                    error={iErrors?.roleRefRole}
                                    touched={iTouched?.roleRefRole}
                                />
                            },
                            'utilisateurRefUti': (aPourRoleValues) => {
                                const index = formik.values.aPourRoles.findIndex(ap => ap === aPourRoleValues);
                                const canManageRecord = isAdmin() /*|| (managedOperationIds.includes(aPourRoleValues.operationRefOperation) && canManageOperationUsers)*/;

                                return canManageRecord &&
                                    <div className="text-center">
                                        <button className="btn btn-danger flex-grow-1" type="button" onClick={() => {
                                            const newAPourRoles = [...formik.values.aPourRoles];
                                            newAPourRoles[index] = {
                                                ...aPourRoleValues,
                                                deleted: true
                                            };

                                            formik.setValues({
                                                ...formik.values,
                                                aPourRoles: newAPourRoles
                                            })
                                        }}><i className="fa fa-minus" />
                                        </button>
                                    </div>;
                            }
                        }}
                        keyExtractor={(ap) => `role-list-${ap.refAPourRole ? ap.refAPourRole : `${ap.roleRefRole}-${ap.operationRefOperation}`}`}
                        leftCustomView={<>
                            <h5 className="mb-0 color-green">Rôles <small>({visibleAPRUnique?.length})</small></h5>
                        </>}
                        rightCustomView={<>
                            {isAdmin() && <div className="text-right">
                                <button className="btn btn-success btn-sm text-white px-5" type="button" onClick={() => {
                                    formik.setValues({
                                        ...formik.values,
                                        aPourRoles: [...formik.values.aPourRoles, {
                                            operationRefOperation: 0,
                                            roleRefRole: 0,
                                            new: true
                                        }]
                                    })
                                }}>
                                    <i className="fa fa-plus" />
                                </button>
                            </div>}
                        </>}
                    />
                    {hasValidationErrors && <div className="alert alert-warning font-weight-bold">
                    Merci de vérifier que tous les champs obligatoires <b>(*)</b> sont bien renseignés
                    </div>}

                    <div className="d-flex my-5">
                        <button className="btn btn-secondary flex-grow-1" type="submit"
                            disabled={!formik.isValid}>
                            Sauvegarder{' '}
                            <i className="fa fa-save" />
                        </button>
                    </div>
                </form>
            </div>
            <Footer />
        </Animated>
    );
};
